import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import ImageStudioModal from "components/ImageStudio/ImageStudioModal";

const ImageStudioContext = createContext(null);

/**
 * Global image studio - one modal for crop, adjust, compress, and KAI enhance.
 * openImageStudio() returns a Promise<File|null> (null if cancelled).
 */
export function ImageStudioProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState({});
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result ?? null);
  }, []);

  const openImageStudio = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOptions(opts);
      setOpen(true);
    });
  }, []);

  const value = useMemo(() => ({ openImageStudio }), [openImageStudio]);

  return (
    <ImageStudioContext.Provider value={value}>
      {children}
      <ImageStudioModal
        open={open}
        source={options.source}
        aspectPreset={options.aspectPreset || "free"}
        contextLabel={options.contextLabel}
        onCancel={() => close(null)}
        onComplete={(file, meta) => {
          if (file && meta) {
            file.studioMeta = meta;
          }
          if (options.onComplete) options.onComplete(file, meta);
          close(file);
        }}
      />
    </ImageStudioContext.Provider>
  );
}

export function useImageStudio() {
  const ctx = useContext(ImageStudioContext);
  if (!ctx) {
    throw new Error("useImageStudio must be used within ImageStudioProvider");
  }
  return ctx;
}

/** Safe hook - returns null openImageStudio if provider missing (legacy paths). */
export function useImageStudioOptional() {
  return useContext(ImageStudioContext);
}
